import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  FormControl,
  InputLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import IELogo from '../assets/IE_Logo.png';
import TPCLogo from '../assets/TPC_Logo.png';
import { toZonedTime, format } from 'date-fns-tz';

const DEFAULT_FIRE_ALERT_DISTANCE_MILES = 15;
const MAX_FIRE_ALERT_DISTANCE_MILES = 15;

const AlertManager = ({ open, onClose, selectedSite, supabase, onSettingsChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [siteTimezone, setSiteTimezone] = useState('UTC');
  const [siteLatitude, setSiteLatitude] = useState(null);
  const [siteLongitude, setSiteLongitude] = useState(null);
  const [alertSettings, setAlertSettings] = useState({
    is_active: true,
    wind_metric: 'gust',
    wind_threshold_mph: 25,
    pm25_threshold_category: 'Unhealthy',
    pm10_threshold_category: 'Unhealthy',
    cooldown_minutes: 120,
    active_time_start: '00:00',
    active_time_end: '23:59',
    // Fire alert settings
    wildfire_enabled: true,
    fire_alert_distance_miles: DEFAULT_FIRE_ALERT_DISTANCE_MILES,
    fire_exclude_rx_burns: true
  });

  // PM2.5 categories
  const pm25Categories = [
    { value: 'Good', label: 'Good (0-9 μg/m³)', range: '0-9' },
    { value: 'Moderate', label: 'Moderate (9.1-35.4 μg/m³)', range: '9.1-35.4' },
    { value: 'Unhealthy for Sensitive Groups', label: 'Unhealthy for Sensitive Groups (35.5-55.4 μg/m³)', range: '35.5-55.4' },
    { value: 'Unhealthy', label: 'Unhealthy (55.5-125.4 μg/m³)', range: '55.5-125.4' },
    { value: 'Very Unhealthy', label: 'Very Unhealthy (125.5-225.4 μg/m³)', range: '125.5-225.4' },
    { value: 'Hazardous', label: 'Hazardous (225.5+ μg/m³)', range: '225.5+' }
  ];

  // PM10 categories
  const pm10Categories = [
    { value: 'Good', label: 'Good (0-54 μg/m³)', range: '0-54' },
    { value: 'Moderate', label: 'Moderate (55-154 μg/m³)', range: '55-154' },
    { value: 'Unhealthy for Sensitive Groups', label: 'Unhealthy for Sensitive Groups (155-254 μg/m³)', range: '155-254' },
    { value: 'Unhealthy', label: 'Unhealthy (255-354 μg/m³)', range: '255-354' },
    { value: 'Very Unhealthy', label: 'Very Unhealthy (355-424 μg/m³)', range: '355-424' },
    { value: 'Hazardous', label: 'Hazardous (425+ μg/m³)', range: '425+' }
  ];

  // Fetch alert settings when dialog opens
  useEffect(() => {
    if (open && selectedSite) {
      fetchAlertSettings();
    }
  }, [open, selectedSite]);

  const fetchAlertSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch site timezone, latitude, and longitude
      const { data: siteData, error: siteError } = await supabase
        .from('sites')
        .select('timezone, latitude, longitude')
        .eq('site_name', selectedSite)
        .single();

      if (siteError && siteError.code !== 'PGRST116') {
        throw siteError;
      }

      if (siteData) {
        if (siteData.timezone) {
          setSiteTimezone(siteData.timezone);
        } else {
          console.warn(`No timezone found for ${selectedSite}, using default UTC`);
        }

        if (siteData.latitude !== null && siteData.latitude !== undefined) {
          setSiteLatitude(siteData.latitude);
        }

        if (siteData.longitude !== null && siteData.longitude !== undefined) {
          setSiteLongitude(siteData.longitude);
        }
      }

      // Fetch alert thresholds
      const { data: thresholdData, error: thresholdError } = await supabase
        .from('alert_thresholds')
        .select('*')
        .eq('site_name', selectedSite)
        .single();

      if (thresholdError && thresholdError.code !== 'PGRST116') {
        throw thresholdError;
      }

      if (thresholdData) {
        // Convert kph to mph for display (1 kph = 0.621371 mph)
        const windThresholdMph = thresholdData.wind_threshold_kph ? 
          Math.round(thresholdData.wind_threshold_kph * 0.621371) : 25;
        
        setAlertSettings({
          is_active: thresholdData.is_active ?? true,
          wind_metric: thresholdData.wind_metric || 'gust',
          wind_threshold_mph: windThresholdMph,
          pm25_threshold_category: thresholdData.pm25_threshold_category || 'Unhealthy',
          pm10_threshold_category: thresholdData.pm10_threshold_category || 'Unhealthy',
          cooldown_minutes: thresholdData.cooldown_minutes || 120,
          active_time_start: thresholdData.active_time_start ? thresholdData.active_time_start.substring(0, 5) : '00:00',
          active_time_end: thresholdData.active_time_end ? thresholdData.active_time_end.substring(0, 5) : '23:59',
          wildfire_enabled: thresholdData.wildfire_enabled ?? true,
          fire_alert_distance_miles: Math.min(
            Math.max(thresholdData.fire_alert_distance_miles ?? DEFAULT_FIRE_ALERT_DISTANCE_MILES, 0),
            MAX_FIRE_ALERT_DISTANCE_MILES
          ),
          fire_exclude_rx_burns: thresholdData.fire_exclude_rx_burns ?? true
        });
      }
    } catch (err) {
      console.error('Error fetching alert settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Convert mph to kph for storage (1 mph = 1.60934 kph)
      const windThresholdKph = alertSettings.wind_threshold_mph * 1.60934;
      const fireAlertDistance = Number(alertSettings.fire_alert_distance_miles);
      const clampedFireAlertDistance = Number.isFinite(fireAlertDistance)
        ? Math.min(Math.max(fireAlertDistance, 0), MAX_FIRE_ALERT_DISTANCE_MILES)
        : DEFAULT_FIRE_ALERT_DISTANCE_MILES;
      
      // Save alert thresholds
      const { error: thresholdError } = await supabase
        .from('alert_thresholds')
        .upsert({
          site_name: selectedSite,
          is_active: alertSettings.is_active,
          wind_metric: alertSettings.wind_metric,
          wind_threshold_kph: windThresholdKph,
          pm25_threshold_category: alertSettings.pm25_threshold_category,
          pm10_threshold_category: alertSettings.pm10_threshold_category,
          cooldown_minutes: alertSettings.cooldown_minutes,
          active_time_start: alertSettings.active_time_start + ':00',
          active_time_end: alertSettings.active_time_end + ':59',
          wildfire_enabled: alertSettings.wildfire_enabled,
          fire_alert_distance_miles: clampedFireAlertDistance,
          fire_exclude_rx_burns: alertSettings.fire_exclude_rx_burns,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'site_name'
        });

      if (thresholdError) throw thresholdError;

      // Clear wildfire cache to force fresh data fetch with new settings
      localStorage.removeItem(`${selectedSite}_lastWildfireData`);
      localStorage.removeItem(`${selectedSite}_lastWildfireSummary`);

      // Notify parent component that settings changed
      if (onSettingsChange) {
        onSettingsChange();
      }

      onClose();
    } catch (err) {
      console.error('Error saving alert settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    '& label.Mui-focused': { color: 'var(--color-brand)' },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: 'var(--color-brand)' }
    }
  };

  const handleExportPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margins = { left: 10, right: 10, top: 26, bottom: 10 };
    const colors = {
      ink: [28, 44, 43],
      muted: [95, 112, 110],
      brand: [15, 118, 110],
      header: [18, 79, 73],
      panelBg: [247, 251, 250],
      panelBorder: [189, 210, 206],
      sectionHead: [227, 244, 242],
      statusOnBg: [223, 244, 235],
      statusOnInk: [22, 120, 79],
      statusOffBg: [255, 236, 214],
      statusOffInk: [158, 84, 19],
    };
    const safeSite = selectedSite || 'Unknown Site';

    // Load images
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    try {
      const ieImg = await loadImage(IELogo);
      const tpcImg = await loadImage(TPCLogo);
      const now = new Date();
      const zonedDate = toZonedTime(now, siteTimezone);
      const exportDate = format(zonedDate, 'MMMM d, yyyy h:mm a zzz');

      const drawPageChrome = (pageNum) => {
        doc.setFillColor(...colors.header);
        doc.rect(0, 0, pageWidth, 22, 'F');

        const ieLogoWidth = 26;
        const ieLogoHeight = (ieImg.height / ieImg.width) * ieLogoWidth;
        doc.addImage(ieImg, 'PNG', margins.left, 5, ieLogoWidth, ieLogoHeight);

        const tpcLogoWidth = 24;
        const tpcLogoHeight = (tpcImg.height / tpcImg.width) * tpcLogoWidth;
        doc.addImage(tpcImg, 'PNG', pageWidth - margins.right - tpcLogoWidth, 5, tpcLogoWidth, tpcLogoHeight);

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Field Safety Dashboard', pageWidth / 2, 11.5, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text('Alert Settings Export', pageWidth / 2, 17, { align: 'center' });

        const footerY = pageHeight - 5.5;
        doc.setDrawColor(...colors.panelBorder);
        doc.setLineWidth(0.2);
        doc.line(margins.left, pageHeight - 8.5, pageWidth - margins.right, pageHeight - 8.5);
        doc.setTextColor(...colors.muted);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`Exported ${exportDate}`, margins.left, footerY);
        doc.text(`Page ${pageNum}`, pageWidth - margins.right, footerY, { align: 'right' });
      };

      const drawSummaryPanel = () => {
        const panelY = 27;
        const panelHeight = 27;

        doc.setDrawColor(...colors.panelBorder);
        doc.setFillColor(...colors.panelBg);
        doc.roundedRect(margins.left, panelY, pageWidth - margins.left - margins.right, panelHeight, 2.5, 2.5, 'FD');

        doc.setTextColor(...colors.brand);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(safeSite, margins.left + 4, panelY + 8);

        doc.setTextColor(...colors.ink);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        const coordsText = `Lat ${siteLatitude !== null ? siteLatitude.toFixed(6) : 'N/A'} | Lon ${siteLongitude !== null ? siteLongitude.toFixed(6) : 'N/A'}`;
        doc.text(coordsText, margins.left + 4, panelY + 13.5);
        doc.text(`Timezone ${siteTimezone}`, margins.left + 4, panelY + 18.5);

        const statusLabel = alertSettings.is_active ? 'Alerts Active' : 'Alerts Inactive';
        const statusWidth = doc.getTextWidth(statusLabel) + 8;
        const statusX = pageWidth - margins.right - statusWidth - 4;
        const statusY = panelY + 6.5;
        const statusBg = alertSettings.is_active ? colors.statusOnBg : colors.statusOffBg;
        const statusInk = alertSettings.is_active ? colors.statusOnInk : colors.statusOffInk;

        doc.setFillColor(...statusBg);
        doc.roundedRect(statusX, statusY, statusWidth, 8.5, 2.5, 2.5, 'F');
        doc.setTextColor(...statusInk);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.text(statusLabel, statusX + statusWidth / 2, statusY + 5.5, { align: 'center' });

        doc.setTextColor(...colors.muted);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text('Configuration snapshot', pageWidth - margins.right - 4, panelY + 18.5, { align: 'right' });
      };

      let yPos = 60;
      let pageNum = 1;
      drawPageChrome(pageNum);
      drawSummaryPanel();

      const ensureSpace = (requiredHeight) => {
        if (yPos + requiredHeight <= pageHeight - margins.bottom - 10) return;
        doc.addPage();
        pageNum += 1;
        drawPageChrome(pageNum);
        yPos = margins.top;
      };

      const drawSection = (title, rows, note = null) => {
        const rowHeight = 5.8;
        const noteLines = note ? doc.splitTextToSize(note, pageWidth - margins.left - margins.right - 8) : [];
        const noteHeight = note ? Math.max(5, noteLines.length * 4) : 0;
        const sectionHeight = 10 + (rows.length * rowHeight) + noteHeight + (note ? 3.5 : 0);

        ensureSpace(sectionHeight + 2);

        doc.setDrawColor(...colors.panelBorder);
        doc.setFillColor(...colors.panelBg);
        doc.roundedRect(margins.left, yPos, pageWidth - margins.left - margins.right, sectionHeight, 2, 2, 'FD');

        doc.setFillColor(...colors.sectionHead);
        doc.roundedRect(margins.left, yPos, pageWidth - margins.left - margins.right, 8, 2, 2, 'F');
        doc.setTextColor(...colors.brand);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(title, margins.left + 3, yPos + 5.4);

        let rowY = yPos + 12;
        rows.forEach(({ label, value }) => {
          doc.setTextColor(...colors.muted);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.text(label, margins.left + 3, rowY);

          doc.setTextColor(...colors.ink);
          doc.setFont('helvetica', 'bold');
          doc.text(String(value), pageWidth - margins.right - 3, rowY, { align: 'right' });
          rowY += rowHeight;
        });

        if (note) {
          doc.setTextColor(...colors.muted);
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8);
          doc.text(noteLines, margins.left + 3, rowY + 1.5);
        }

        yPos += sectionHeight + 3;
      };

      drawSection(
        'Alert Activity',
        [
          { label: 'Master Switch', value: alertSettings.is_active ? 'Enabled' : 'Disabled' },
          { label: 'Start Time', value: `${alertSettings.active_time_start} (${siteTimezone})` },
          { label: 'End Time', value: `${alertSettings.active_time_end} (${siteTimezone})` },
          { label: 'Cooldown', value: `${alertSettings.cooldown_minutes} minutes` },
        ],
        'Alerts are only delivered during the active window, using site-local timezone settings.'
      );

      drawSection(
        'Wind Threshold',
        [
          { label: 'Metric', value: alertSettings.wind_metric },
          { label: 'Threshold', value: `${alertSettings.wind_threshold_mph} mph` },
        ]
      );

      drawSection(
        'Lightning',
        [
          { label: 'Yellow Alert', value: '>12 to 18 miles' },
          { label: 'Orange Alert', value: '>6 to 12 miles' },
          { label: 'Red Alert', value: '6 miles or less' },
        ],
        'Lightning thresholds are fixed in code. Red alerts maintain a mandatory 30-minute hold and issue a red-clear message when the hold expires.'
      );

      drawSection(
        'Wildfire',
        [
          { label: 'Fire Alerts', value: alertSettings.wildfire_enabled ? 'Enabled' : 'Disabled' },
          { label: 'Alert Distance', value: `${alertSettings.fire_alert_distance_miles} miles` },
          { label: 'Exclude Rx Burns', value: alertSettings.fire_exclude_rx_burns ? 'Yes' : 'No' },
        ],
        'When Rx burns are excluded, prescribed fire events are filtered out from wildfire alert triggers.'
      );

      drawSection(
        'Particulate Matter',
        [
          { label: 'PM2.5 Threshold Category', value: alertSettings.pm25_threshold_category },
          { label: 'PM10 Threshold Category', value: alertSettings.pm10_threshold_category },
        ]
      );

      // Save the PDF
      doc.save(`${safeSite}_alert_settings.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          overflow: 'hidden',
          borderRadius: '14px',
        },
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(120deg, var(--color-brand-dark), var(--color-brand))',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
        }}
      >
        <Typography variant="h6">
          Manage Alerts - {selectedSite}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ backgroundColor: '#f7fbf9', py: 2.2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: 'var(--color-brand)' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <Box>
            {/* Master Alert Toggle */}
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              border: alertSettings.is_active ? 'none' : '2px solid #e74c3c',
              borderRadius: 1.5,
              backgroundColor: '#ffffff',
              boxShadow: 'none'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={alertSettings.is_active}
                    onChange={(e) => setAlertSettings({ ...alertSettings, is_active: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'var(--color-brand)',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'var(--color-brand)',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="h6">
                    {alertSettings.is_active ? 'Alerts Active' : 'Alerts Inactive'}
                  </Typography>
                }
              />
              <Typography variant="body2" color="text.secondary">
                Master switch to enable/disable all alerts for this site
              </Typography>
            </Paper>

            {/* Active Time */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 1.5, backgroundColor: '#ffffff', boxShadow: 'none' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Active Hours</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Alerts will only be sent during these hours ({siteTimezone})
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={alertSettings.active_time_start}
                    onChange={(e) => setAlertSettings({ ...alertSettings, active_time_start: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={inputStyle}
                  />
                </Grid>
                <Grid item xs={2} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1">to</Typography>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="End Time"
                    type="time"
                    value={alertSettings.active_time_end}
                    onChange={(e) => setAlertSettings({ ...alertSettings, active_time_end: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={inputStyle}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Alert Thresholds */}
            <Typography variant="h6" sx={{ mb: 2 }}>Alert Thresholds</Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Alert Cooldown */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Alert Cooldown</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Cooldown Period (minutes)"
                  type="number"
                  value={alertSettings.cooldown_minutes}
                  onChange={(e) => setAlertSettings({ ...alertSettings, cooldown_minutes: parseInt(e.target.value) })}
                  fullWidth
                  helperText="Minimum time between alerts of the same type"
                  sx={inputStyle}
                />
              </Grid>

              {/* Wind Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Wind</Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Wind Metric</InputLabel>
                  <Select
                    value={alertSettings.wind_metric}
                    onChange={(e) => setAlertSettings({ ...alertSettings, wind_metric: e.target.value })}
                    label="Wind Metric"
                  >
                    <MenuItem value="speed">Wind Speed</MenuItem>
                    <MenuItem value="gust">Wind Gust</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Threshold (mph)"
                  type="number"
                  value={alertSettings.wind_threshold_mph}
                  onChange={(e) => setAlertSettings({ ...alertSettings, wind_threshold_mph: parseFloat(e.target.value) })}
                  fullWidth
                  sx={inputStyle}
                />
              </Grid>

              {/* Lightning Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Lightning</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Lightning thresholds are fixed in code for now:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Yellow Alert: over 12 miles and up to 18 miles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Orange Alert: over 6 miles and up to 12 miles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Red Alert: 6 miles or less, with a mandatory 30-minute hold before a Red all-clear message can be sent
                </Typography>
              </Grid>

              {/* Fire Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Wildfire</Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={alertSettings.wildfire_enabled}
                      onChange={(e) => setAlertSettings({ ...alertSettings, wildfire_enabled: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'var(--color-brand)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'var(--color-brand)',
                        },
                      }}
                    />
                  }
                  label="Enable Fire Alerts"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Alert Distance (miles)"
                  type="number"
                  value={alertSettings.fire_alert_distance_miles}
                  onChange={(e) => {
                    const parsedValue = parseFloat(e.target.value);
                    const nextValue = e.target.value === ''
                      ? ''
                      : Number.isFinite(parsedValue)
                        ? Math.min(Math.max(parsedValue, 0), MAX_FIRE_ALERT_DISTANCE_MILES)
                        : '';
                    setAlertSettings({ ...alertSettings, fire_alert_distance_miles: nextValue });
                  }}
                  fullWidth
                  disabled={!alertSettings.wildfire_enabled}
                  inputProps={{ min: 0, max: MAX_FIRE_ALERT_DISTANCE_MILES, step: 0.1 }}
                  helperText={`Alert when fires are within this distance, up to ${MAX_FIRE_ALERT_DISTANCE_MILES} miles`}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={alertSettings.fire_exclude_rx_burns}
                      onChange={(e) => setAlertSettings({ ...alertSettings, fire_exclude_rx_burns: e.target.checked })}
                      disabled={!alertSettings.wildfire_enabled}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'var(--color-brand)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'var(--color-brand)',
                        },
                        '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': {
                          color: '#7a9276',
                        },
                        '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#7a9276',
                        },
                      }}
                    />
                  }
                  label="Exclude Rx Burns"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  When enabled, prescribed burns will not trigger alerts
                </Typography>
              </Grid>

              {/* PM Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Particulate Matter</Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>PM2.5 Threshold</InputLabel>
                  <Select
                    value={alertSettings.pm25_threshold_category}
                    onChange={(e) => setAlertSettings({ ...alertSettings, pm25_threshold_category: e.target.value })}
                    label="PM2.5 Threshold"
                  >
                    {pm25Categories.map(cat => (
                      <MenuItem key={cat.value} value={cat.value}>{cat.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>PM10 Threshold</InputLabel>
                  <Select
                    value={alertSettings.pm10_threshold_category}
                    onChange={(e) => setAlertSettings({ ...alertSettings, pm10_threshold_category: e.target.value })}
                    label="PM10 Threshold"
                  >
                    {pm10Categories.map(cat => (
                      <MenuItem key={cat.value} value={cat.value}>{cat.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                onClick={handleExportPDF}
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: 'var(--color-brand)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--color-brand-dark)',
                    boxShadow: 'none',
                  },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  backgroundColor: 'var(--color-brand)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--color-brand-dark)',
                    boxShadow: 'none',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AlertManager;
